<script setup>
name: 'blogSocialShare'
const props = defineProps({
  class: {
    type: String,
    required: false
  }
})

const baseUrl = ref('');
const copied = ref(false);

const getMobileOS = () => {
  if (/windows phone/i.test(navigator.userAgent)) {
    return 'Windows Phone';
  }

  if (/android/i.test(navigator.userAgent)) {
    return 'Android';
  }

  if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
    return 'iOS';
  }

  return '';
}

const isMobileOS = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

const shareLinkViaFacebook = () => {
  let link = baseUrl.value
  let uri = 'https://www.facebook.com/sharer/sharer.php?u=' + link;
  if (isMobileOS()) {
    window.location.href = uri;
    return;
  }
  window.open(uri);
}

const shareLinkViaWhatsApp = () => {
  let link = baseUrl.value
  let uri = 'https://api.whatsapp.com/send?text=' + link;

  if (isMobileOS()) {
    uri = 'whatsapp://send?text=' + link;
    window.location.href = uri;
    return;
  }

  window.open(uri);
}

const shareArticleOnLinkedIn = (title, summary) => {
  let url = baseUrl.value
  let source = baseUrl.value
  let link = "https://www.linkedin.com/shareArticle";
  link += '?mini=true';
  link += '&url=' + encodeURIComponent(url);

  if (title.length && title.length > 1) { link += '&title=' + encodeURIComponent(title); }
  if (summary.length && summary.length > 1) { link += '&summary=' + encodeURIComponent(summary); }
  if (source.length && source.length > 1) { link += '&source=' + encodeURIComponent(source); }

  if (isMobileOS()) {
    window.location.href = link;
    return;
  }

  window.open(link);
}

const shareArticleOnTwitter = () => {
  let uri = `http://twitter.com/share?text=${baseUrl.value}`
  window.open(uri);
}

async function copyToClipboard() {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(baseUrl.value);
    } else {
      fallbackCopyToClipboard(baseUrl.value);
    }
  } catch (error) {
    fallbackCopyToClipboard(baseUrl.value);
  }
  copied.value = true;
  setTimeout(() => { copied.value = false; }, 2000);
}

function fallbackCopyToClipboard(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

onMounted(() => {
  baseUrl.value = window.location.href;
})
</script>

<template>
  <div class="share-container" :class="class">
    <a class="social-share__link social-share__link--facebook" @click="shareLinkViaFacebook">
      <span class="social-share__link__icon icon-sm-facebook"></span>
    </a>
    <a class="social-share__link social-share__link--linkedin" @click="shareArticleOnLinkedIn('', '');">
      <span class="social-share__link__icon icon-sm-linkedin"></span>
    </a>
    <a class="social-share__link social-share__link--twitter" @click="shareArticleOnTwitter">
      <span class="social-share__link__icon bi bi-twitter-x"></span>
    </a>
    <a class="social-share__link social-share__link--whatsapp" @click="shareLinkViaWhatsApp">
      <span class="social-share__link__icon icon-whatsapp"></span>
    </a>
    <a class="social-share__link social-share__link--copy-link" @click="copyToClipboard">
      <span v-if="copied" class="copy-popover">Copied!</span>
    </a>
  </div>
</template>

<style lang="scss" scoped>
.share-container {
  display: flex;
  align-items: center;

  .social-share__link {
    margin: .2rem;
    text-decoration: none;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: .5rem;
    opacity: 1;
    transition: opacity 0.5s;

    &:not(:last-child){
      margin-right: 20px;
    }

    &.social-share__link--facebook {
      .social-share__link__icon{
        color: #3b5998;
      }
    }

    &.social-share__link--linkedin {
      .social-share__link__icon{
        color: #007bb6;
      }
    }

    &.social-share__link--whatsapp {
      .social-share__link__icon{
        color: #1BD741;
      }
    }

    &.social-share__link--copy-link {
      position: relative;

      .copy-popover {
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        background: $color-labels;
        color: #fff;
        font-size: 0.75rem;
        padding: 3px 8px;
        border-radius: 4px;
        white-space: nowrap;
        pointer-events: none;

        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 4px solid transparent;
          border-top-color: $color-labels;
        }
      }

      &::before {
        content: '';
        background-repeat: no-repeat;
        background-position: center center;
        width: 20px;
        height: 20px;
        background-size: 20px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30.295' height='30.268' viewBox='0 0 30.295 30.268'%3E%3Cdefs%3E%3Cstyle%3E .prefix__cls-1%7Bfill:none;stroke:%23000;stroke-linecap:round;stroke-linejoin:round;stroke-width:3px%7D %3C/style%3E%3C/defs%3E%3Cg id='prefix__copy_link_icon' data-name='copy link icon' transform='translate(1.5 1.5)'%3E%3Cpath id='prefix__Path_432' d='M15 18.005a6.82 6.82 0 0 0 10.285.737l4.092-4.092a6.82 6.82 0 1 0-9.645-9.645l-2.345 2.333' class='prefix__cls-1' data-name='Path 432' transform='translate(-4.081 -3.008)'/%3E%3Cpath id='prefix__Path_433' d='M19.368 16.227a6.82 6.82 0 0 0-10.285-.737l-4.092 4.093a6.82 6.82 0 1 0 9.645 9.645l2.332-2.333' class='prefix__cls-1' data-name='Path 433' transform='translate(-2.993 -3.958)'/%3E%3C/g%3E%3C/svg%3E%0A");
      }
    }

    .social-share__link__icon {
      color: black;
      font-size: 1.25rem;
    }

    &:hover {

      &::before {
        filter: opacity(1);
      }

      .social-share__link__icon {
        opacity: 0.7;
      }
    }

    &::before {
      filter: opacity(0.65);
      -webkit-transition: .25s -webkit-filter linear;
      -moz-transition: .25s -moz-filter linear;
      -moz-transition: .25s filter linear;
      -ms-transition: .25s -ms-filter linear;
      -o-transition: .25s -o-filter linear;
      transition: .25s filter linear, .25s -webkit-filter linear;
    }
  }
}
</style>